import { BriefcaseIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { Column } from "@/components/Column";
import { EmptyState } from "@/components/EmptyState";
import { SortableHeader } from "@/components/SortableHeader";
import { JobCell } from "@/components/dashboard/DashboardTable";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TableBody,
  TableHeader,
  TableRow,
  TableCell,
} from "@/components/ui/table";

import { dashboard } from "@/lib/dashboard";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DashboardJob, DashboardSortKey, DashboardSortState } from "@/types";

const SKELETON_ROW_COUNT = 7;

// Single-column jobs list shown when the calendar is expanded
export const DashboardTableMinimized = ({
  sort,
  sortedJobs,
  onSortChange,
  isLoading,
}: {
  sort: DashboardSortState | null;
  sortedJobs: DashboardJob[];
  onSortChange: (next: DashboardSortState | null) => void;
  isLoading: boolean;
}) => {
  const navigate = useNavigate();

  const handleSort = (key: DashboardSortKey) => {
    onSortChange(dashboard.getNextSortState(sort, key));
  };

  const navigateToPipeline = (job: DashboardJob) => {
    mixpanelUtils.trackRowClick("Dashboard job", MIXPANEL_SCREENS.DASHBOARD, {
      job_id: job.id,
      company_id: job.companyId,
    });
    void navigate(dashboard.jobPipelinePath(job));
  };

  const renderBody = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Column className="gap-1.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-20" />
            </Column>
          </TableCell>
        </TableRow>
      ));
    }
    if (sortedJobs.length === 0) {
      return (
        <TableRow>
          <TableCell className="p-0">
            <EmptyState
              className="rounded-b-lg bg-slate-50 py-12"
              description="Jobs assigned to this staffer will appear here."
              Icon={BriefcaseIcon}
              title="No jobs :("
            />
          </TableCell>
        </TableRow>
      );
    }

    return sortedJobs.map((job) => (
      <TableRow
        key={job.id}
        className="group cursor-pointer"
        onClick={() => {
          navigateToPipeline(job);
        }}
      >
        <JobCell job={job} showLogo={false} />
      </TableRow>
    ));
  };

  const render = () => (
    <Column className="flex h-full min-h-0 w-56 shrink-0 flex-col">
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden shadow-none">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              <col />
            </colgroup>
            <TableHeader className="sticky top-0 z-20 bg-slate-50 [&_th]:h-12 [&_th]:overflow-hidden [&_th]:border-b [&_th]:border-slate-200 [&_th]:px-3 [&_th]:py-0 [&_tr_th:first-child]:rounded-tl-lg [&_tr_th:last-child]:rounded-tr-lg">
              <TableRow>
                <SortableHeader
                  sortKey="jobs"
                  label="Jobs"
                  sort={sort}
                  onSort={handleSort}
                />
              </TableRow>
            </TableHeader>
            <TableBody className="[&_td]:overflow-hidden [&_td]:!px-3 [&_td]:!py-3 [&_tr:last-child_td:first-child]:rounded-bl-lg [&_tr:last-child_td:last-child]:rounded-br-lg [&_tr:last-child_td]:border-b-0 [&_tr_td]:border-b [&_tr_td]:border-slate-100">
              {renderBody()}
            </TableBody>
          </table>
        </div>
      </Card>
    </Column>
  );

  return render();
};
